import {
  Avatar,
  AvatarGroup,
  Button,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  MenuOptionGroup,
  Spacer,
  Stack,
  Text,
  useDisclosure
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { useStatsigClient } from '@statsig/react-bindings';

import { pluralized, getFormattedNamesListString } from '@util/helpers';
import { useInitiativeById } from '@services/query-services/initiatives.query-service';
import { createConversationViaCollection } from '@services/query-services/conversations.query-service';
import { useUserStore } from '@store/app.store';
import CenteredLayout from '@layouts/centered.layout';
import {
  HorizontalDotMenuIcon,
  SFLockedIconOutlined,
  SFSparkleStarIcon,
  SFSquareAndPencil,
  SFTrashIcon,
  SFUnlockedIconOutlined
} from '@assets/icons';
import DeleteCollectionModal from '@components/collection/delete-collection-modal.component';
import CreateCollectionModal from '@components/collection/create-collection-modal.component';
import FeedSummaries from '@components/feed/feed-summaries.component';
import ShareCollectionModal from '@components/collection/share-collection-modal.component';

export default function CollectionPage() {
  const { checkGate } = useStatsigClient();
  const isExplorerEnabled = checkGate('explorer-access-buttons');
  const { collectionId } = useParams();
  const navigate = useNavigate();
  const { data: collection } = useInitiativeById(collectionId, { enabled: !!collectionId });
  const user = useUserStore((state) => state.user);
  const isUserCreator = user?.id === collection?.creator?.id;
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);
  const isPrivate = collection?.visibility !== 'Tenant';
  const deleteConfirmationDisclosure = useDisclosure();
  const editCollectionNameDisclosure = useDisclosure();
  const shareCollectionDisclosure = useDisclosure();

  const collaboratorsText = useMemo(() => {
    const names = [
      collection?.creator?.name,
      ...(collection?.collaborators?.map((c) => c.name) || [])
    ].filter(Boolean);

    return getFormattedNamesListString(names);
  }, [collection]);

  const summaries = useMemo(() => {
    return (
      <FeedSummaries
        cardSearchBody={{ collection_ids: [collectionId] }}
        noResultsText=''
        gap={6}
        cardProps={{ isExpanded: true }}
      />
    );
  }, [collection]);

  return (
    <CenteredLayout>
      <Stack
        p={11}
        gap={12}
      >
        <Stack gap={6}>
          <HStack>
            <Text variant='headline-lighter-md'>{collection?.title}</Text>
            <Spacer />
            <Menu placement='bottom-end'>
              <MenuButton
                as={IconButton}
                icon={<HorizontalDotMenuIcon />}
                colorScheme='surface'
                variant='reskinGhost'
                isRound
                size='sm'
              />
              <MenuList>
                <MenuOptionGroup title='Collection Settings'>
                  <MenuItem
                    icon={<SFSquareAndPencil />}
                    onClick={() => editCollectionNameDisclosure.onOpen()}
                  >
                    Edit collection name
                  </MenuItem>
                  <MenuItem
                    icon={<SFTrashIcon />}
                    onClick={() => deleteConfirmationDisclosure.onOpen()}
                  >
                    Delete collection
                  </MenuItem>
                </MenuOptionGroup>
              </MenuList>
            </Menu>
          </HStack>
          <HStack
            color='surface.onSurfaceVariant'
            gap={6}
          >
            <HStack>
              <Icon as={isPrivate ? SFLockedIconOutlined : SFUnlockedIconOutlined} />
              <Text variant='body-600-md'>{isPrivate ? 'Private' : 'Public'} Collection</Text>
            </HStack>
            <Text variant='body-600-md'>{pluralized('item', collection?.num_of_cards ?? 0)}</Text>
          </HStack>
          <HStack>
            <AvatarGroup
              size='xsm'
              max={2}
            >
              {[collection?.creator, ...(collection?.collaborators || [])]?.map((p) => (
                <Avatar
                  src={p?.avatar_url || ''}
                  key={`${p?.email || p?.name}-avatar`}
                  name={p?.name}
                />
              ))}
            </AvatarGroup>
            <Text
              variant='body-500-sm'
              color='brandPrimary.primary'
            >
              {collaboratorsText}
            </Text>
            {isUserCreator && (
              <Button
                onClick={() => shareCollectionDisclosure.onOpen()}
                variant='reskinSecondary'
                size='reskinSm'
              >
                Share
              </Button>
            )}
          </HStack>
        </Stack>

        {isExplorerEnabled && (
          <HStack gap={3}>
            <Button
              isLoading={isCreatingConversation}
              w='fit-content'
              variant='reskinPrimary'
              leftIcon={<SFSparkleStarIcon boxSize={4} />}
              size='md'
              onClick={async () => {
                setIsCreatingConversation(true);
                const conversation = await createConversationViaCollection(collectionId);
                setIsCreatingConversation(false);
                navigate(`/explorer/${conversation?.id}`);
              }}
            >
              Chat with this collection
            </Button>
            <Button
              w='fit-content'
              variant='reskinSecondary'
              size='md'
              onClick={() => {
                navigate(`/conversations-history?initiator_data_id=${collectionId}`);
              }}
            >
              View Previous Chats
            </Button>
          </HStack>
        )}
        {summaries}
      </Stack>
      <CreateCollectionModal
        editingCollection={collection}
        disclosure={editCollectionNameDisclosure}
      />
      <DeleteCollectionModal
        onDelete={() => navigate('/collections')}
        collectionId={collectionId}
        disclosure={deleteConfirmationDisclosure}
      />
      {isUserCreator && (
        <ShareCollectionModal
          collection={collection}
          disclosure={shareCollectionDisclosure}
        />
      )}
    </CenteredLayout>
  );
}
